import { useAuth } from '@/hooks/useAuth';
import { Box, Collapsible, HStack, Icon, Image, Separator, Spacer, Spinner, Stack, Text } from '@chakra-ui/react';
import { ReactElement, SyntheticEvent, useState } from 'react';
import { NavLink } from 'react-router';

import { FiArrowUpRight } from 'react-icons/fi';

import { AppRoute, MATRIX_DOCK_GOLD_URL, PUSD_URL, ROYCO_DOMAIN_URL, STAKING_URL } from '@/constants/shared';
import { useCurrentUserData } from '@/hooks/useCurrentUserData';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { useRole } from '@/hooks/useRole';
import { useUser } from '@/hooks/useUser';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { isTruthy } from '@/utils';
import { useAppKit } from '@reown/appkit/react';
import { AiOutlineSwap } from 'react-icons/ai';
import { FiGrid } from 'react-icons/fi';
import {
  PiGraduationCapBold,
  PiMinusCircleBold,
  PiPlusCircleBold,
  PiShieldBold,
  PiStarBold,
  PiStarFill,
} from 'react-icons/pi';
import { RiHomeLine } from 'react-icons/ri';
import { TbBuildingBridge2 } from 'react-icons/tb';
import { ConnectWalletButton } from './connect-wallet-button';
import { HomeLink } from './home-link';
import { ConnectedWalletModal } from './modals/connected-wallet-modal';

type PageNavItem = {
  name: string;
  icon?: ReactElement;
  to: string;
  showExternalIcon?: boolean;
  shouldShowNotification?: boolean;
  hidden?: boolean;
};

export type PageNavSection = {
  name: string;
};

type Props = {
  variant?: 'mobile' | 'desktop';
};
export const Navbar = ({ variant = 'desktop' }: Props) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin } = useRole();
  const { hasSpunToday } = useDailySpinOnchain();
  const { currentUserData } = useCurrentUserData();
  const { favorites } = useUser();
  const shouldShowSeasonClaimPages = Boolean(currentUserData?.seasonOneAllocation?.calculatedTotal);
  const shouldShowInitialClaimNotification =
    // if they have an any total allo they will have some initial, and if they havent claimed it yet
    (Boolean(currentUserData?.seasonOneAllocation?.calculatedTotal) &&
      !currentUserData?.seasonOneAllocation?.airdropClaim?.initialClaimTimestamp) ||
    // OR if they won a battle group and haven't claimed it yet
    (Boolean(currentUserData?.seasonOneAllocation?.battleGroupWonAt) &&
      !currentUserData?.seasonOneAllocation?.airdropClaim?.mainnetClaimTimestamp);

  const PUBLIC_PAGES: PageNavItem[] = [
    { name: 'Home', icon: <RiHomeLine />, to: AppRoute.Home },
    { name: 'Bridge', icon: <TbBuildingBridge2 />, to: AppRoute.BridgeWithDefaults },
    { name: 'Swap', icon: <AiOutlineSwap />, to: AppRoute.Swap },
    {
      name: 'Stake',
      icon: <Image src="/assets/home/plume-feather-gray.svg" />,
      to: STAKING_URL,
      showExternalIcon: true,
    },
    {
      name: 'Buy Gold',
      icon: <Image src="/assets/home/icons/gold-icon.svg" />,
      to: MATRIX_DOCK_GOLD_URL,
      showExternalIcon: true,
    },
    { name: 'Level Up', icon: <PiGraduationCapBold />, to: AppRoute.Quests },
    { name: 'Discover', icon: <FiGrid />, to: AppRoute.Apps },
  ];

  const ADMIN_PAGES: PageNavItem[] = [
    { name: 'Admin', icon: <PiShieldBold />, to: AppRoute.AdminAirdropSummary },
    { name: 'Optimizer', icon: <PiShieldBold />, to: AppRoute.Optimizer },
  ];

  const PRIVATE_PAGES: PageNavItem[] = [
    {
      name: 'Mint pUSD',
      to: PUSD_URL,
      showExternalIcon: true,
    },
    {
      name: 'Daily Spin',
      to: AppRoute.DailySpin,
      shouldShowNotification: !hasSpunToday && isAuthenticated,
    },
    { name: 'Royco', icon: <StarButton name="Royco" />, to: ROYCO_DOMAIN_URL, showExternalIcon: true },
    isAuthenticated ? { name: 'Profile', to: AppRoute.MyProfile } : null,
    { name: 'Leaderboard', to: AppRoute.Leaderboard },
    shouldShowSeasonClaimPages
      ? {
          name: 'Airdrop',
          to: AppRoute.SeasonOneClaim,
          shouldShowNotification: shouldShowInitialClaimNotification,
        }
      : null,
    {
      name: 'Guardians of Plume',
      to: AppRoute.GuardiansOfPlume,
    },
  ].filter(isTruthy);

  const FAVORITE_PAGES: PageNavItem[] = PRIVATE_PAGES.filter((page) => favorites.includes(page.name));

  const MORE_PAGES: PageNavItem[] = PRIVATE_PAGES.filter((page) => !favorites.includes(page.name));
  return (
    <Stack direction="column" textAlign={'center'} h="full" justifyContent="space-between">
      <Stack direction="column" gap={0} pb={variant === 'mobile' ? '120px' : 0} w="full">
        {variant === 'desktop' && (
          <HStack justify="space-between" mb={6}>
            <HomeLink />
            {!isAuthenticated && <ConnectWalletButton variant="small" />}
          </HStack>
        )}

        {PUBLIC_PAGES.map((page) => {
          const { name, to, icon, showExternalIcon } = page;
          return <StyledNavLink key={name} name={name} to={to} icon={icon} showExternalIcon={showExternalIcon} />;
        })}

        {isAdmin && (
          <>
            <Separator flex={1} my={2} />
            {ADMIN_PAGES.map((page) => {
              const { name, to, icon, showExternalIcon } = page;
              return <StyledNavLink key={name} name={name} to={to} icon={icon} showExternalIcon={showExternalIcon} />;
            })}
          </>
        )}

        <>
          <Separator flex={1} my={2} />
          {FAVORITE_PAGES.map((page) => {
            return <StyledNavLink key={page.name} {...page} />;
          })}
          {MORE_PAGES.length ? <MorePageCollapsible pages={MORE_PAGES} /> : null}
        </>
      </Stack>
      {variant === 'desktop' && isAuthenticated && (
        <Box w="full" position="sticky" bottom={0} backdropFilter="blur(10px)" bg="rgba(255, 255, 255, 0.7)">
          <ConnectedWalletModal />
        </Box>
      )}
    </Stack>
  );
};

const StyledNavLink = ({
  name,
  to,
  icon,
  showExternalIcon,
  shouldShowNotification,
}: {
  name: string;
  to: string;
  icon?: ReactElement;
  showExternalIcon?: boolean;
  shouldShowNotification?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      _hover={{
        backgroundColor: '#F1F5F980', // Light blue background on hover
        color: 'fg', // Darker text color on hover
      }}
      rounded="md"
      color="fg.muted"
      h={58}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NavLink
        key={name}
        to={to}
        target={to.startsWith('/') ? undefined : '_blank'}
        onClick={() => {
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedNav, {
            nav_item: name,
          });
        }}
        style={({ isActive }) => ({
          // TODO: think about how we want to handle inline styles like this
          // do we want to add tailwind? or wait until we see if there's more instances of this
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '10px 12px',
          borderRadius: '8px',
          backgroundColor: isActive ? '#F1F5F9' : 'transparent',
          textDecoration: 'none',
          color: isActive ? '#111111' : 'inherit',
          fontWeight: '400',
          gap: '13px',
          fontSize: '16px',
          lineHeight: '24px',
          height: '100%',
          letterSpacing: '-0.18px',
        })}
      >
        <HStack w="full">
          {icon ? (
            <Icon boxSize={5} mr={1.5}>
              {icon}
            </Icon>
          ) : (
            <StarButton name={name} />
          )}
          <Text>{name}</Text>
          <Spacer flex={1} />
          {hovered && showExternalIcon && <FiArrowUpRight color="#989898" style={{ marginLeft: '-7px' }} />}
          {shouldShowNotification && (
            <Box width="16px" height="16px" position="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ animation: 'pulse 2s infinite' }}
              >
                <circle cx="8" cy="8" r="8" fill="#FDECDD" />
                <circle cx="8" cy="8" r="4" fill="#B95700" />
              </svg>
            </Box>
          )}
        </HStack>
      </NavLink>
    </Box>
  );
};

const MorePageCollapsible = ({ pages }: { pages: PageNavItem[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible.Root onOpenChange={(d) => setOpen(d.open)} open={open}>
      <Collapsible.Trigger
        py={2.5}
        px={3}
        cursor="pointer"
        w="full"
        color="fg.muted"
        _hover={{
          backgroundColor: 'bg.subtle', // Light gray background on hover
          color: 'fg', // Darker text color on hover
        }}
        rounded="md"
        h={58}
      >
        <HStack>
          <Icon boxSize={5} mr={1.5}>
            {open ? <PiMinusCircleBold /> : <PiPlusCircleBold />}
          </Icon>
          {open ? 'Less' : 'More'}
        </HStack>
      </Collapsible.Trigger>
      <Collapsible.Content>
        {pages.map((p) => (
          <StyledNavLink key={p.name} {...p} />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const StarButton = ({ name }: { name: string }) => {
  const { user, isPendingUser, addFavorite, removeFavorite } = useUser();
  const isFavorited = user?.userExperienceMeta?.favorites?.includes(name);
  const { open } = useAppKit();
  const onClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!user) open();
    else if (isFavorited) removeFavorite(name);
    else addFavorite(name);
  };

  if (isPendingUser) return <Spinner />;

  // if not favorited return empty star
  return (
    <Icon onClick={onClick} boxSize={5} mr={1.5}>
      {isFavorited ? <PiStarFill /> : <PiStarBold />}
    </Icon>
  );
};
